import React, { useRef, useEffect, useState, useCallback } from 'react';
import { useDrag } from 'react-dnd';

const DraggableHotspot = ({ hotspot, isSelected, onSelect, onChange }) => {
    const ref = useRef(null);
    const initialPos = useRef({ x: 0, y: 0 });
    const [isResizing, setIsResizing] = useState(false);
    const currentHandleRef = useRef(null);

    const [{ isDragging }, drag, dragPreview] = useDrag({
        type: 'HOTSPOT',
        item: { id: hotspot.id, type: 'HOTSPOT' },
        canDrag: !isResizing && isSelected,
        collect: (monitor) => ({
            isDragging: monitor.isDragging()
        })
    });

    useEffect(() => {
        dragPreview(<div></div>); // 隐藏拖拽预览
    }, [dragPreview]);

    const onMouseMove = useCallback((e) => {
        if (!isResizing) return;

        const dx = e.clientX - initialPos.current.x;
        const dy = e.clientY - initialPos.current.y;
        let newWidth = initialPos.current.width;
        let newHeight = initialPos.current.height;
        let newX = initialPos.current.left;
        let newY = initialPos.current.top;

        switch (currentHandleRef.current) {
            case 'nw':
                newWidth = initialPos.current.width - dx;
                newHeight = initialPos.current.height - dy;
                newX = initialPos.current.left + dx;
                newY = initialPos.current.top + dy;
                break;
            case 'ne':
                newWidth = initialPos.current.width + dx;
                newHeight = initialPos.current.height - dy;
                newY = initialPos.current.top + dy;
                break;
            case 'sw':
                newWidth = initialPos.current.width - dx;
                newHeight = initialPos.current.height + dy;
                newX = initialPos.current.left + dx;
                break;
            case 'se':
                newWidth = initialPos.current.width + dx;
                newHeight = initialPos.current.height + dy;
                break;
        }

        // 确保热区不会太小
        if (newWidth >= 20 && newHeight >= 20) {
            onChange({
                x: newX,
                y: newY,
                width: newWidth,
                height: newHeight
            });
        }
    }, [isResizing, hotspot, onChange]);

    const onMouseUp = useCallback(() => {
        setIsResizing(false);
        currentHandleRef.current = null;
    }, []);

    useEffect(() => {
        if (!ref.current) return;

        const element = ref.current;

        const onMouseDown = (e) => {
            const handle = e.target.getAttribute('data-handle');
            if (handle) {
                setIsResizing(true);
                currentHandleRef.current = handle;
                initialPos.current = {
                    x: e.clientX,
                    y: e.clientY,
                    width: hotspot.width,
                    height: hotspot.height,
                    left: hotspot.x,
                    top: hotspot.y
                };
                e.stopPropagation(); // 阻止事件冒泡，避免触发拖动
                e.preventDefault();
            } else {
                // 如果点击的不是调整大小手柄，则选中热区
                onSelect(hotspot);
            }
        };

        element.addEventListener('mousedown', onMouseDown);
        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);

        return () => {
            element.removeEventListener('mousedown', onMouseDown);
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', onMouseUp);
        };
    }, [hotspot, onChange, isResizing, onMouseMove, onMouseUp]);

    drag(ref);

    return (
        <div
            ref={ref}
            className={`hotspot ${isSelected ? 'selected' : ''} ${isDragging ? 'dragging' : ''}`}
            style={{
                left: `${hotspot.x}px`,
                top: `${hotspot.y}px`,
                width: `${hotspot.width}px`,
                height: `${hotspot.height}px`,
                opacity: isDragging ? 0.5 : 1,
                position: 'absolute',
                background: 'rgba(0, 82, 217, 0.1)',
                border: isSelected ? '2px solid var(--semi-color-danger)' : '2px solid var(--semi-color-primary)',
                boxShadow: isSelected ? '0 0 0 2px var(--semi-color-danger)' : 'none',
                cursor: 'move',
                userSelect: 'none'
            }}
            onClick={(e) => {
                e.stopPropagation();
            }}
        >
            <div
                className="resizeHandle"
                data-handle="nw"
                style={{
                    position: 'absolute',
                    width: '12px',
                    height: '12px',
                    background: 'var(--semi-color-primary)',
                    border: '2px solid white',
                    borderRadius: '50%',
                    top: '-6px',
                    left: '-6px',
                    cursor: 'nw-resize',
                    zIndex: 2
                }}
            />
            <div
                className="resizeHandle"
                data-handle="ne"
                style={{
                    position: 'absolute',
                    width: '12px',
                    height: '12px',
                    background: 'var(--semi-color-primary)',
                    border: '2px solid white',
                    borderRadius: '50%',
                    top: '-6px',
                    right: '-6px',
                    cursor: 'ne-resize',
                    zIndex: 2
                }}
            />
            <div
                className="resizeHandle"
                data-handle="sw"
                style={{
                    position: 'absolute',
                    width: '12px',
                    height: '12px',
                    background: 'var(--semi-color-primary)',
                    border: '2px solid white',
                    borderRadius: '50%',
                    bottom: '-6px',
                    left: '-6px',
                    cursor: 'sw-resize',
                    zIndex: 2
                }}
            />
            <div
                className="resizeHandle"
                data-handle="se"
                style={{
                    position: 'absolute',
                    width: '12px',
                    height: '12px',
                    background: 'var(--semi-color-primary)',
                    border: '2px solid white',
                    borderRadius: '50%',
                    bottom: '-6px',
                    right: '-6px',
                    cursor: 'se-resize',
                    zIndex: 2
                }}
            />
        </div>
    );
};

export default DraggableHotspot;